import styled from '@emotion/styled';
import type { MicrositeProfile } from '~/model';
import {
	css_background,
	css_gap,
	css_if,
	css_large,
	css_letter,
	css_medium_up,
	css_small_down,
	css_small_up,
	css_stroke,
	css_text,
} from '~/emotion/theme';
import MicrositeResponsiveImg from '~/components/MicrositeResponsiveImg';
import { LoadPriority } from '~/hooks/useLoadPriority';

const Grid = styled.div`
	display: grid;
	gap: ${css_gap('grid')};

	&:has(> * + *) {
		@media (${css_large}) {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
`;

const Card = styled.article<{ single?: boolean }>`
	display: flex;
	flex-direction: column;
	background: ${css_background()};
	padding: ${css_gap('content')};
	gap: ${css_gap('content')};

	// @media (${css_medium_up}) {
	// 	flex-direction: ${css_if('single', 'column', 'row')};
	// }

	@media (${css_small_up}) {
		flex-direction: row;
	}

	@media (${css_medium_up}) {
		flex-direction: ${css_if('single', 'column', 'row')};
	}

	@media (${css_small_down}) {
		align-items: center;
	}
`;

const ImageFrame = styled.div<{ single?: boolean }>`
	width: 10rem;
	height: 10rem;
	overflow: hidden;
	flex-shrink: 0;
	border: 1px solid ${css_stroke()};
`;

const Details = styled.div`
	@media (${css_small_down}) {
		text-align: center;
	}
`;

const Name = styled.h3`
	text-transform: uppercase;
	letter-spacing: ${css_letter('md')};
	font-size: 1.1rem;
	font-weight: 500;
`;

const Subtitle = styled.p`
	color: ${css_text('weak')};
	text-transform: uppercase;
	letter-spacing: ${css_letter('md')};
	font-size: 0.7rem;
	padding-top: 0.3rem;
`;

const PrimaryInfo = styled.div`
	display: grid;
	gap: 0.5rem;
	color: ${css_text()};
	letter-spacing: ${css_letter('sm')};
	font-size: 0.7rem;
	padding-top: ${css_gap('content')};
`;

const SecondaryInfo = styled.div`
	display: grid;
	color: ${css_text()};
	letter-spacing: ${css_letter('sm')};
	font-size: 0.7rem;
	gap: 0.3rem;
	padding-top: ${css_gap('content')};
`;

const Website = styled.a`
	color: ${css_text()};
	letter-spacing: ${css_letter('sm')};
	font-size: 0.7rem;
	font-weight: 500;
`;

const License = styled.div`
	color: ${css_text('weak')};
	font-size: 0.625rem;
`;

export function BetaAgentCard({ profile, single }: { profile: MicrositeProfile; single?: boolean }) {
	const subtitle = profile.title ?? profile.company ?? profile.organization;

	return (
		<Card single={single}>
			{profile.headshot && (
				<ImageFrame single={single}>
					<MicrositeResponsiveImg priority={LoadPriority.ON_LOAD} image={profile.headshot} />
				</ImageFrame>
			)}
			<Details>
				<Name>{profile.name}</Name>
				{subtitle && <Subtitle>{subtitle}</Subtitle>}
				<PrimaryInfo>
					{profile.organization && profile.organization !== subtitle && <div>{profile.organization}</div>}
					{profile.company && profile.company !== subtitle && <div>{profile.company}</div>}
					{profile.phone && <div>{profile.phone}</div>}
					{profile.email && <div>{profile.email}</div>}
				</PrimaryInfo>
				<SecondaryInfo>
					{profile.license && <License>MLS #{profile.license}</License>}
					{profile.website && <Website href={profile.website}>{new URL(profile.website).hostname}</Website>}
				</SecondaryInfo>
			</Details>
		</Card>
	);
}

export default function BetaAgentList({ profiles }: { profiles: MicrositeProfile[] }) {
	return (
		<Grid>
			{profiles.map((profile) => (
				<BetaAgentCard key={profile.email ?? profile.name} profile={profile} />
			))}
		</Grid>
	);
}
